import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { FormField } from '../../../components/Formulaire/FormField';
import FormSection from '../../../components/Formulaire/FormSection';
import { apiService } from '../../../components/Config/api.service';
import { notify } from '../../../components/Utils/Notifications';
import AmountInput from '../../../components/Utils/AmountInput';
import { MdOutlineCalculate } from 'react-icons/md';
import ContractSimulationPanel from './ContractSimulationPanel';
import ContratSigne from './ContratSigne';
import { useCountryConfig } from '../../../components/Config/useCountryConfig';

const ContractForm = ({ currentContract, employee, refreshContract, onSuccess }) => {
    const { t } = useTranslation();
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [contract, setContract] = useState({
        contractName: '',
        employee: '',
        poste: '',
        startDate: false,
        endDate: false,
        contractType: 'CDI',
        status: 'open',
        category: '',
        baseSalary: '',
        convention: '',
        isExecutive: false,
        sursalaire: 0,
        netSalary: 0,
        is_executive: false,
        return_date_leave: false,
        leave_days_carried_over: 0,
        part_ir: false,
        trimf: false,
        manager: false,
        // Prérempli avec le défaut de la société à la création (Yros/Support#39).
        transport_premium: '',
        ipm_contribution: 0,
        ipm_contribution_pat: 0,
        indemnite_responsabilite: 0,
        taux_fssp_responsabilite: 0,
        payment_mode: 'virement',
    });
    const [categories, setCategories] = useState([]);
    const [conventions, setConventions] = useState([]);
    const [filteredCategories, setFilteredCategories] = useState([]);
    const [showSimulation, setShowSimulation] = useState(false);

    const countryConfig = useCountryConfig();
    const labels = countryConfig.labels;
    const flexibleBaseSalary = !!countryConfig.flexibleBaseSalary;
    const fsspResponsabilite = !!countryConfig.fsspResponsabilite;

    useEffect(() => {
        if (currentContract?.id) {
            setErrorMessage('');
            setSuccessMessage('');
            apiService.getElementById('/contract', currentContract.id).then(response => {
                const contractData = response.data;
                const category = categories.find(category => category.value === contractData.category);
                const convention = conventions.find(convention => convention.value === category?.conventionType);

                if (category) {
                    contractData.category = category.value;
                    contractData.baseSalary = category.baseSalary;
                    contractData.is_executive = category.is_executive;
                }
                if (convention) {
                    contractData.convention = convention.value;
                }
                // Un contrat sans contract_type_id renvoie contractType: false/null.
                // On ne devine pas un type (CDI par defaut serait souvent faux) : le
                // select affiche l'option vide et force l'utilisateur a choisir.
                if (!contractData.contractType) {
                    contractData.contractType = '';
                }
                setContract(contractData);
                setErrorMessage('');
            }).catch(error => {
                console.error('Error fetching contract:', error);
                setErrorMessage(error.userMessage || t('contractEmpForm.fetchContractError'));
            });
        }
    }, [currentContract, categories, conventions, t]);

    // Nouveau contrat : la prime de transport part du défaut de la société
    // (hérité de la société mère pour une filiale sans surcharge).
    const isNewContract = !currentContract?.id;
    useEffect(() => {
        if (!isNewContract) return;
        apiService.getElement('/company/info')
            .then(response => {
                const amount = response.data?.transport_premium_effective;
                if (amount === undefined || amount === null) return;
                setContract(prev => (prev.transport_premium === '' ? { ...prev, transport_premium: amount } : prev));
            })
            .catch(error => console.error('Error fetching company transport premium:', error));
    }, [isNewContract]);

    useEffect(() => {
        apiService.getElement('/conventions')
            .then(response => {
                const conventionOptions = response.data.conventions.map(convention => ({
                    value: convention.id,
                    label: convention.name
                }));
                setConventions(conventionOptions);

                const categoryList = response.data.categories.map(category => ({
                    value: category.id,
                    label: category.name,
                    baseSalary: category.salary_for173h,
                    conventionType: category.convention_type,
                    is_executive: category.is_executive
                }));
                setCategories(categoryList);
            })
            .catch(error => {
                console.error('Error fetching conventions and categories:', error);
                setErrorMessage(error.userMessage || t('contractEmpForm.fetchConventionsError'));
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setContract(prev => ({
            ...prev,
            [name]: value,
            ...(name === 'contractType' && (value === 'CDI' || value === 'JOURNALIER') ? { endDate: '' } : {})
        }));
    };

    const handleMontantChange = (name, newValue) => {
        setContract(prev => ({ ...prev, [name]: newValue }));
    };

    const handleConventionChange = (selectedOption) => {
        setContract({
            ...contract,
            convention: selectedOption.value,
            category: '',
            baseSalary: ''
        });
        const filtered = categories.filter(category => category.conventionType === selectedOption.value);
        setFilteredCategories(filtered);
    };

    const handleCategoryChange = (selectedOption) => {
        setContract({
            ...contract,
            category: selectedOption.value,
            baseSalary: selectedOption.baseSalary,
            is_executive: selectedOption.is_executive
        });
    };

    const applySimulatedSursalaire = (sursalaire) => {
        setContract(prev => ({ ...prev, sursalaire }));
        setShowSimulation(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMessage('');
        setSuccessMessage('');

        const contractData = {
            ...contract,
            employee: contract.employee || employee?.id,
        };
        // Champ vide : le backend applique le défaut de la société.
        if (contractData.transport_premium === '' || contractData.transport_premium === null) {
            delete contractData.transport_premium;
        }

        if (currentContract) {
            apiService
                .updateElementById('/contract', currentContract.id, contractData)
                .then((response) => {
                    notify(t('contractEmpForm.updateSuccess'), '', 2);
                    refreshContract();
                    onSuccess(t('contractEmpForm.updateSuccess'));
                })
                .catch((error) => {
                    notify(error.userMessage || t('contractEmpForm.updateError'), '', 3);
                });
        } else {
            apiService
                .saveElement('/contract', contractData)
                .then((response) => {
                    notify(t('contractEmpForm.createSuccess'), '', 2);
                    refreshContract();
                    onSuccess(t('contractEmpForm.createSuccess'));
                })
                .catch((error) => {
                    notify(error.userMessage || t('contractEmpForm.createError'), '', 3);
                });
        }
    };

    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            borderColor: state.isFocused ? 'var(--color-primary)' : provided.borderColor,
            boxShadow: state.isFocused ? '0 0 0 1px var(--color-primary)' : provided.boxShadow,
            '&:hover': {
                borderColor: state.isFocused ? 'var(--color-primary)' : provided.borderColor
            },
            borderRadius: '0.375rem',
            padding: '2px',
            minHeight: '42px'
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected
                ? 'var(--color-primary)'
                : state.isFocused
                    ? 'rgba(var(--color-primary-rgb), 0.1)'
                    : provided.backgroundColor,
            color: state.isSelected ? 'white' : provided.color
        })
    };

    return (
        <form onSubmit={handleSubmit} className="p-6">
            {errorMessage && <div className="text-center text-red-500 mb-4 p-3 bg-red-50 rounded-lg">{errorMessage}</div>}
            {successMessage && <div className="text-center text-green-500 mb-4 p-3 bg-green-50 rounded-lg">{successMessage}</div>}

            <FormSection title={t('contractEmpForm.sectionContractInfo')} color="blue">
                {currentContract ? (
                    <>
                    <FormField label={t('contractEmpForm.contractReference')}>
                        <input
                            type="text"
                            name="contractName"
                            value={contract.contractName || ''}
                            readOnly
                            className="input input-bordered w-full bg-gray-100 cursor-not-allowed focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                        />
                    </FormField>
                    <FormField label={t('contractEmpForm.status')}>
                        <select
                            name="status"
                            value={contract.status || ''}
                            onChange={handleChange}
                            className="select select-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                            required>
                            <option value="draft">{t('contractEmpForm.statusNew')}</option>
                            <option value="open">{t('contractEmpForm.statusRunning')}</option>
                            <option value="close">{t('contractEmpForm.statusEnded')}</option>
                            <option value="cancel">{t('contractEmpForm.statusSuspended')}</option>
                        </select>
                    </FormField>
                    </>
                ) : null}

                <FormField label={t('contractEmpForm.contractType')}>
                    <select
                        name="contractType"
                        value={contract.contractType || ''}
                        onChange={handleChange}
                        className="select select-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                        required>
                        <option value="" disabled>{t('contractEmpForm.selectContractType')}</option>
                        <option value="CDI">CDI</option>
                        <option value="CDD">CDD</option>
                        <option value="JOURNALIER">Journalier</option>
                    </select>
                </FormField>

                <FormField label={t('contractEmpForm.paymentMode')}>
                    <select
                        name="payment_mode"
                        value={contract.payment_mode || 'virement'}
                        onChange={handleChange}
                        className="select select-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                    >
                        <option value="virement">{t('contractEmpForm.virement')}</option>
                        <option value="espece">{t('contractEmpForm.espece')}</option>
                        <option value="cheque">{t('contractEmpForm.cheque')}</option>
                        <option value="mobile_money">{t('contractEmpForm.mobileMoney')}</option>
                    </select>
                </FormField>
            </FormSection>

            <FormSection title={t('contractEmpForm.sectionEmployeeInfo')} color="green">
                <FormField label={t('contractEmpForm.employee')}>
                    <input
                        type="text"
                        value={employee?.name || ''}
                        className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                        readOnly
                    />
                </FormField>

                <FormField label={t('contractEmpForm.position')}>
                    <input
                        type="text"
                        name="poste"
                        value={employee?.poste || ''}
                        onChange={handleChange}
                        className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                        readOnly
                    />
                </FormField>

                {currentContract && (
                    <>
                        <FormField label={`${labels.part_ir}:`}>
                            <input
                                type="text"
                                name="part_ir"
                                value={contract.part_ir || ''}
                                className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                                readOnly
                            />
                        </FormField>

                        <FormField label={`${labels.trimf}:`}>
                            <input
                                type="text"
                                name="trimf"
                                value={contract.trimf || ''}
                                className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                                readOnly
                            />
                        </FormField>
                    </>
                )}
            </FormSection>

            <FormSection title={t('contractEmpForm.sectionPeriod')} color="orange">
                <FormField label={t('contractEmpForm.startDate')}>
                    <input
                        type="date"
                        name="startDate"
                        required
                        value={contract.startDate || ''}
                        onChange={handleChange}
                        className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                    />
                </FormField>
                <FormField label={t('contractEmpForm.endDate')}>
                    <input
                        type="date"
                        name="endDate"
                        min={contract.startDate || undefined}
                        value={contract.endDate || ''}
                        onChange={handleChange}
                        required={contract.contractType === 'CDD'}
                        disabled={contract.contractType === 'CDI' || contract.contractType === 'JOURNALIER'}
                        className={`input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20 ${contract.contractType === 'CDI' || contract.contractType === 'JOURNALIER' ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                    />
                </FormField>
                <FormField label={t('contractEmpForm.leaveReturnDate')}>
                    <input
                        type="date"
                        name="return_date_leave"
                        min={contract.startDate || undefined}
                        value={contract.return_date_leave || ''}
                        onChange={handleChange}
                        required
                        className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                    />
                </FormField>
                <FormField label={t('contractEmpForm.carriedOverLeaveDays')}>
                    <input
                        type="number"
                        name="leave_days_carried_over"
                        value={contract.leave_days_carried_over || 0}
                        onChange={handleChange}
                        step="0.5"
                        className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                    />
                </FormField>
            </FormSection>

            <FormSection title={t('contractEmpForm.sectionSalaryDetails')} color="purple">
                {!flexibleBaseSalary && (
                    <>
                        <FormField label={t('contractEmpForm.convention')}>
                            <Select
                                options={conventions}
                                onChange={handleConventionChange}
                                value={conventions.find(convention => convention.value === contract.convention)}
                                styles={customSelectStyles}
                                className="w-full"
                                required
                            />
                        </FormField>
                        <FormField label={t('contractEmpForm.category')}>
                            <Select
                                options={filteredCategories}
                                onChange={handleCategoryChange}
                                value={categories.find(category => category.value === contract.category)}
                                styles={customSelectStyles}
                                className="w-full"
                                required
                            />
                        </FormField>
                    </>
                )}

                <FormField label={t('contractEmpForm.baseSalary')}>
                    <AmountInput
                        value={contract.baseSalary || ''}
                        onChange={flexibleBaseSalary ? (newValue) => handleMontantChange('baseSalary', newValue) : () => { }}
                        className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                        readOnly={!flexibleBaseSalary}
                    />
                </FormField>

                <FormField label={t('contractEmpForm.transportPremium')}>
                    <AmountInput
                        name="transport_premium"
                        value={contract.transport_premium}
                        onChange={(newValue) => handleMontantChange('transport_premium', newValue)}
                        className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                    />
                </FormField>

                {fsspResponsabilite && (
                    <FormField label={t('contractEmpForm.responsibilityAllowance')}>
                        <AmountInput
                            name="indemnite_responsabilite"
                            value={contract.indemnite_responsabilite}
                            onChange={(newValue) => handleMontantChange('indemnite_responsabilite', newValue)}
                            className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                        />
                    </FormField>
                )}

                {fsspResponsabilite && (
                    <FormField label={t('contractEmpForm.fsspResponsibilityRate')}>
                        <input
                            type="number"
                            name="taux_fssp_responsabilite"
                            min="0"
                            max="100"
                            step="0.01"
                            value={contract.taux_fssp_responsabilite ?? 0}
                            onChange={handleChange}
                            className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                        />
                        <span className="text-xs text-gray-500">
                            {t('contractEmpForm.fsspResponsibilityRateHint')}
                        </span>
                    </FormField>
                )}

                <FormField label={t('contractEmpForm.ipmEmployeeContribution')}>
                    <AmountInput
                        name="ipm_contribution"
                        value={contract.ipm_contribution}
                        onChange={(newValue) => handleMontantChange('ipm_contribution', newValue)}
                        className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                    />
                </FormField>

                <FormField label={t('contractEmpForm.ipmEmployerContribution')}>
                    <AmountInput
                        name="ipm_contribution_pat"
                        value={contract.ipm_contribution_pat}
                        onChange={(newValue) => handleMontantChange('ipm_contribution_pat', newValue)}
                        className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                    />
                </FormField>

                <FormField >
                    <span className="text-sm text-gray-500">{t('contractEmpForm.isExecutive')}</span>
                    <input
                        type="checkbox"
                        name="is_executive"
                        checked={contract.is_executive}
                        onChange={(e) => setContract({ ...contract, is_executive: e.target.checked })}
                        className="checkbox "
                    />
                </FormField>

                <FormField >
                    <span className="text-sm text-gray-500">{t('contractEmpForm.isManager')}</span>
                    <input
                        type="checkbox"
                        name="manager"
                        checked={contract.manager}
                        onChange={(e) => setContract({ ...contract, manager: e.target.checked })}
                        className="checkbox "
                    />
                </FormField>


            </FormSection>

            {/* Sursalaire et simulation de salaire (Yros/Support#42) */}
            <FormField label={t('contractEmpForm.overSalary')}>
                <div className="flex gap-2 items-center">
                    <AmountInput
                        name="sursalaire"
                        value={contract.sursalaire || ''}
                        onChange={(newValue) => handleMontantChange('sursalaire', newValue)}
                        className="input input-bordered w-[250px] focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                    />
                    {!flexibleBaseSalary && !showSimulation && (
                        <button
                            type="button"
                            onClick={() => setShowSimulation(true)}
                            className="btn btn-sm px-3 py-1 bg-[--color-primary] text-white hover:bg-[--bg-hover] transition-colors rounded-md"
                            title={t('contractEmpForm.simulateTitle')}
                        >
                            <MdOutlineCalculate className="mr-1.5" size={16} />
                            <span className="text-xs">{t('contractEmpForm.simulate')}</span>
                        </button>
                    )}
                </div>
            </FormField>
            {showSimulation && (
                <ContractSimulationPanel
                    contract={contract}
                    employee={employee}
                    onApply={applySimulatedSursalaire}
                    onClose={() => setShowSimulation(false)}
                />
            )}

            {/* Yros/Kavero#489 : le PDF signé se dépose sur un contrat existant. */}
            {currentContract?.id && (
                <ContratSigne
                    contratId={currentContract.id}
                    nom={contract.signedDocument || null}
                    onChange={(nom) => setContract((precedent) => ({ ...precedent, signedDocument: nom }))}
                />
            )}

            <div className="flex justify-center mt-8">
                <button
                    type="submit"
                    className="bg-[--color-primary] hover:bg-[--bg-hover] rounded-full text-white px-12 py-2 font-medium transition-all duration-200 shadow-sm"
                >
                    {currentContract ? t('contractEmpForm.updateContract') : t('contractEmpForm.createContract')}
                </button>
            </div>
        </form>
    );
}

export default ContractForm;